import { useMemo } from 'react';

import { Combinations } from '@givebacks/utils/types';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { StoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import type { ConsumerCheckoutCartsResponse } from './checkout.types';

type UseShowConsumerCheckoutArgs<T> = {
  checkoutId: string;

  include?: Combinations<'fee_groups' | 'summary' | 'givinga'>;
  pending_card_type?: string;

  /** Cart Ids can be provided to have the checkout view ONLY return carts that are in the cart_ids array */
  cartIds?: string[];
} & Omit<UseQueryOptions<ConsumerCheckoutCartsResponse, Error, T>, 'queryKey' | 'queryFn'>;

type UseShowConsumerCheckoutFromCartArgs<T> = Omit<UseShowConsumerCheckoutArgs<T>, 'checkoutId'>;

/**
 * Fetches the checkout cart views for a given checkout id, if no checkout id is provided, the query will not be enabled.
 */
const useShowConsumerCheckout = <T = ConsumerCheckoutCartsResponse>({
  checkoutId,
  cartIds,
  include = 'summary,fee_groups,givinga',
  pending_card_type,
  select,
  enabled = true,
  ...queryOptions
}: UseShowConsumerCheckoutArgs<T>) => {
  const getConsumerCheckoutCartViews = async () => {
    const { data } = await StoreApi.get<ConsumerCheckoutCartsResponse>(
      `/consumer/checkouts/${checkoutId}`,
      {
        params: {
          cart_ids: cartIds ? cartIds.join(',') : undefined,
          include,
          pending_card_type,
        },
      }
    );
    return data;
  };

  return useQuery({
    queryKey: [
      ...storeKeys.id('user').consumer.checkout.id(checkoutId).detail,
      { cartIds, include, pending_card_type },
    ],
    queryFn: getConsumerCheckoutCartViews,
    select,
    enabled: !!checkoutId && enabled,
    ...queryOptions,
  });
};

/**
 * Fetches and returns a checkout view for the given cart ids,
 * the cart Ids must be carts that have not been purchased yet.
 */
const useShowConsumerCheckoutFromCartIds = <T = ConsumerCheckoutCartsResponse>({
  cartIds,
  include = 'summary,fee_groups,givinga',
  select,
  enabled = true,
  ...queryOptions
}: UseShowConsumerCheckoutFromCartArgs<T>) => {
  const hasCartIds = useMemo(() => cartIds && cartIds.length > 0, [cartIds]);
  const getConsumerCheckoutCartViews = async () => {
    const { data } = await StoreApi.get<ConsumerCheckoutCartsResponse>('/consumer/checkouts/new', {
      params: {
        cart_ids: cartIds ? cartIds.join(',') : undefined,
        include,
      },
    });
    return data;
  };

  return useQuery({
    queryKey: [...storeKeys.id('user').consumer.checkout.detail, { cartIds, include }],
    queryFn: getConsumerCheckoutCartViews,
    select,
    enabled: !!hasCartIds && enabled,
    ...queryOptions,
  });
};

export { useShowConsumerCheckout, useShowConsumerCheckoutFromCartIds };
